import React, { useState, useRef, useMemo } from 'react';

import { Country } from 'country-state-city';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button, Input } from '#/components/ui';
import { useAppContext } from '#/context';
import { ROUTES } from '#/routes';
import { ComboBox } from '.';

const GlobalSearch = () => {
    const { companiesQueryParams, setCompaniesQueryParams, businessTypesData } = useAppContext();
    const containerRef = useRef(null);

    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [businessType, setBusinessType] = useState('');
    const [keyword, setKeyword] = useState('');

    const countries = useMemo(
        () =>
            Country.getAllCountries().map(c => ({
                value: c.name,
                label: c.name,
            })),
        []
    );

    const businessTypesArray = useMemo(
        () =>
            Object.values(businessTypesData || {})?.map(bt => ({
                value: bt.name,
                label: bt.name,
            })),
        [businessTypesData]
    );

    const isButtonDisabled = !keyword && !selectedCountry && !businessType;

    const handleSearch = () => {
        const updatedParams = {
            ...companiesQueryParams,
            ...(keyword && { keyword }),
            ...(selectedCountry && { country: [selectedCountry] }),
            ...(businessType && { business_type: [businessType] }),
        };

        setCompaniesQueryParams(updatedParams);

        handleClose();
        navigate(ROUTES.HOME);
    };

    function handleKeywordChange(e) {
        setKeyword(e.target.value);
    }

    function handleOpen() {
        setOpen(true);
    }

    function handleClose() {
        setOpen(false);
        setKeyword('');
        setSelectedCountry('');
        setBusinessType('');
    }

    return (
        <div className='relative w-full max-w-[564px]' ref={containerRef}>
            <div
                onClick={handleOpen}
                className='relative flex flex-col w-full rounded-lg border border-gray300 h-[42px] bg-white cursor-pointer z-10'
            >
                <span className='absolute text-gray400 left-3 top-2'>Find new buyers</span>
                <Search className='absolute w-[18px] h-[18px] text-gray500 right-3 top-3' />
            </div>

            {open && (
                <>
                    <div className='fixed inset-0 z-20 bg-black/10 backdrop-blur-sm' onClick={handleClose} />
                    <div className='relative bottom-6 flex z-50 flex-col w-full rounded-lg  h-[42px] bg-white cursor-pointer'>
                        <span className='absolute text-gray400 left-3 top-2'>Find new buyers</span>
                        <Search className='absolute w-[18px] h-[18px] text-gray500 right-3 top-3' />
                    </div>
                    <div className='absolute z-50 top-16 w-full bg-white rounded-lg shadow-lg p-10'>
                        <div className='flex flex-col gap-8'>
                            <h1 className='text-center text-textPrimary font-cormorant font-semibold text-2xl'>
                                Let's find new buyers for your business
                            </h1>
                            <div className='flex flex-col gap-4'>
                                <div className='space-y-2'>
                                    <span htmlFor='name' className='text-sm font-medium text-gray700 text-nowrap'>
                                        Do you know the name of the contact or business you are searching for?
                                    </span>
                                    <Input
                                        id='name'
                                        name='name'
                                        type='text'
                                        placeholder='Type the name here'
                                        className='w-full px-4 text-gray700 placeholder-gray400 py-3 border border-borderGray rounded-lg shadow-sm placeholder:text-sm'
                                        value={keyword}
                                        onChange={handleKeywordChange}
                                    />
                                </div>

                                <div className='space-y-2'>
                                    <label className='text-sm font-medium text-gray700'>
                                        In which location are you searching for buyers?
                                    </label>
                                    <ComboBox
                                        value={selectedCountry}
                                        onChange={setSelectedCountry}
                                        placeholder='Search for location'
                                        options={countries}
                                    />
                                </div>

                                <div className='space-y-2'>
                                    <label className='text-sm font-medium text-gray700'>
                                        What type of business are you looking for?
                                    </label>
                                    <ComboBox
                                        value={businessType}
                                        onChange={setBusinessType}
                                        placeholder='Search for business type'
                                        options={businessTypesArray}
                                    />
                                </div>
                            </div>

                            <Button disabled={isButtonDisabled} onClick={handleSearch}>
                                Find Buyers
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export { GlobalSearch };
